import { Light } from './Light.js';
import { SpotLight as BABYLON_SpotLight } from '@inter-ikea-betrakta-babylonjs/core/Lights/spotLight.js';
import { calculate_world_transform, calculate_direction_from_parent_world_transform } from '../../../utility/TransformHelper.js';
import { vector_3 } from '../../../utility/Conversion.js';
import { TrackedVec3, degrees, radians, vector3_pool, matrix_pool, clamp } from '../../../../Math.js';
import { IVector3, LightType } from '../../../../api.js';
import { SpotLightShadowController } from '../shadows/SpotLightShadowController.js';
import { SpotLightVisualizer } from '../debug_visualizers/SpotLightVisualizer.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
var SpotLightIntensityMode;
(function (SpotLightIntensityMode) {
    SpotLightIntensityMode[SpotLightIntensityMode["LUMEN"] = 0] = "LUMEN";
    SpotLightIntensityMode[SpotLightIntensityMode["CANDELA"] = 1] = "CANDELA";
    SpotLightIntensityMode[SpotLightIntensityMode["DIRECT"] = 2] = "DIRECT";
})(SpotLightIntensityMode || (SpotLightIntensityMode = {}));
export class SpotLight extends Light {
    constructor(viewer, position = { x: 0, y: 0, z: 0 }, direction = { x: 0, y: -1, z: 0 }, outer_angle = Math.PI / 3, inner_angle = 0.0) {
        super(viewer);
        this._direction = new TrackedVec3();
        this._expermiental_distance_falloff_enabled = true;
        this._shadow_angle_radians = null;
        this.type = LightType.SPOT_LIGHT;
        this._babylon_light = new BABYLON_SpotLight(`Light_${this.id} (SpotLight)`, vector_3(position), vector_3(direction), outer_angle, 2, this._viewer.scene);
        this.shadow = new SpotLightShadowController(this, this._babylon_light, this._light_system);
        this._direction.onchange = () => this.mark_dirty(Light.DIRTY_DIRECTION);
        this.position = position;
        this._direction.set(direction.x, direction.y, direction.z);
        this._babylon_light.innerAngle = inner_angle;
        // Set the falloff type to GLTF, the default does not behave like we want it to.
        this._babylon_light.falloffType = BABYLON_SpotLight.FALLOFF_GLTF;
        this._intensity_mode = SpotLightIntensityMode.DIRECT;
        this._physical_intensity = this.intensity;
        // This is to make sure Babylon's light's photometric scale is 1.0, i.e. we take all responsibility for setting intensity, and we get the intensity we set.
        this._babylon_light.intensityMode = BABYLON_SpotLight.INTENSITYMODE_LUMINOUSINTENSITY;
    }
    set expermiental_distance_falloff_enabled(value) {
        if (this._expermiental_distance_falloff_enabled === value)
            return;
        this._expermiental_distance_falloff_enabled = value;
        // Note that we changed the lightFragment shader so FALLOFF_STANDARD does not take distance into consideration.
        this._babylon_light.falloffType = value ? BABYLON_SpotLight.FALLOFF_GLTF : BABYLON_SpotLight.FALLOFF_STANDARD;
        this._babylon_light._markMeshesAsLightDirty();
        this._viewer.mark_scene_dirty();
    }
    get direction() {
        return this._direction;
    }
    set direction(value) {
        this._direction.set(value.x, value.y, value.z);
    }
    look_at_local(local_position, local_target) {
        const direction = IVector3.normalize(IVector3.subtract(local_target, local_position));
        this.position = local_position;
        this._direction.copy(direction);
    }
    look_at_world(world_position, world_target) {
        const local_position = vector3_pool.fetch();
        const local_target = vector3_pool.fetch();
        // If the SpotLight has a parent, convert the world position and target to local space
        if (this._parent) {
            const inverse_parent_world_matrix = matrix_pool.fetch();
            this._parent.get_world_matrix().invertToRef(inverse_parent_world_matrix);
            const world_pos_babylon = vector3_pool.fetch().fromIVector3(world_position);
            BABYLON_Vector3.TransformCoordinatesToRef(world_pos_babylon, inverse_parent_world_matrix, local_position);
            const world_target_babylon = vector3_pool.fetch().fromIVector3(world_target);
            BABYLON_Vector3.TransformCoordinatesToRef(world_target_babylon, inverse_parent_world_matrix, local_target);
            matrix_pool.return(inverse_parent_world_matrix);
            vector3_pool.return(world_pos_babylon, world_target_babylon);
        }
        else {
            local_position.set(world_position.x, world_position.y, world_position.z);
            local_target.set(world_target.x, world_target.y, world_target.z);
        }
        this.look_at_local(local_position, local_target);
        vector3_pool.return(local_position, local_target);
    }
    get inner_angle_radians() {
        return this._babylon_light.innerAngle;
    }
    set inner_angle_radians(angle) {
        if (this._babylon_light.innerAngle === angle)
            return;
        this.set_angles(angle, this.outer_angle_radians);
    }
    get outer_angle_radians() {
        return this._babylon_light.angle;
    }
    set outer_angle_radians(angle) {
        if (this._babylon_light.angle === angle)
            return;
        this.set_angles(this.inner_angle_radians, angle);
    }
    // This getter is here to provide compatibility with DirectedShadowPointLight for their commonly used SpotLightShadowController
    get shadow_angle_radians() {
        var _a;
        return Math.min(this.outer_angle_radians, (_a = this._shadow_angle_radians) !== null && _a !== void 0 ? _a : this.outer_angle_radians);
    }
    set_custom_shadow_angle_radians(angle) {
        if (angle === this._shadow_angle_radians)
            return;
        this._shadow_angle_radians = angle;
        this.mark_dirty(Light.DIRTY_SHADOW_MAP);
    }
    set_custom_shadow_angle_degrees(angle) {
        this.set_custom_shadow_angle_radians((angle === null) ? null : radians(angle));
    }
    get inner_angle_degrees() {
        return degrees(this.inner_angle_radians);
    }
    set inner_angle_degrees(angle) {
        this.inner_angle_radians = radians(angle);
    }
    get outer_angle_degrees() {
        return degrees(this.outer_angle_radians);
    }
    set outer_angle_degrees(angle) {
        this.outer_angle_radians = radians(angle);
    }
    get intensity() {
        return super.intensity;
    }
    set intensity(intensity) {
        this._intensity_mode = SpotLightIntensityMode.DIRECT;
        this._physical_intensity = intensity;
        super.intensity = intensity;
    }
    set_intensity_lumen(lumen) {
        // The same amount of lumen spread out over a narrower cone angle results
        // in higher intensity (Cd). When the cone angle is 2 PI, the spotlight
        // behaves like a point light.
        this._intensity_mode = SpotLightIntensityMode.LUMEN;
        this._physical_intensity = lumen;
        // Important to use super.intensity here, as otherwise we'd overwrite our intensity mode to "direct"
        super.intensity = lumen / SpotLight.calculate_light_weighted_surface_area(this.inner_angle_radians, this.outer_angle_radians);
    }
    set_intensity_candela(candela) {
        // Since we use candela as the internal unit,
        // we don't need to consider the cone angle when
        // setting the intensity in candela.
        this._intensity_mode = SpotLightIntensityMode.CANDELA;
        this._physical_intensity = candela;
        this.intensity = candela;
    }
    update() {
        this.update_color_and_intensity();
        this.update_parent_material();
        if (this._parent) {
            const parent_updated = this.is_dirty(Light.DIRTY_PARENT_ADDED |
                Light.DIRTY_PARENT_TRANSFORM |
                Light.DIRTY_POSITION);
            if (parent_updated) {
                const world_position = this.get_world_position();
                const world_direction = this.get_world_direction();
                this._babylon_light.position.set(world_position.x, world_position.y, world_position.z);
                this._babylon_light.direction.set(world_direction.x, world_direction.y, world_direction.z);
                this.mark_dirty(Light.DIRTY_SHADOW_MAP);
            }
        }
        else {
            if (this.is_dirty(Light.DIRTY_POSITION)) {
                this._babylon_light.position.set(this.position.x, this.position.y, this.position.z);
                this.mark_dirty(Light.DIRTY_SHADOW_MAP);
            }
        }
        if (this.is_dirty(Light.DIRTY_ANGLE)) {
            this.mark_dirty(Light.DIRTY_SHADOW_MAP);
        }
        if (this.is_dirty(Light.DIRTY_DIRECTION)) {
            // Negate direction here because Babylon flips spotlight direction when you change the handedness.
            const world_direction = this.get_world_direction();
            this._babylon_light.direction.set(world_direction.x, world_direction.y, world_direction.z);
            this.mark_dirty(Light.DIRTY_SHADOW_MAP);
        }
        if (this.is_dirty(Light.DIRTY_SHADOW_MAP)) {
            this._babylon_light.shadowAngleScale = this._shadow_angle_radians === null
                ? 1
                : Math.min(1.0, this._shadow_angle_radians / this.outer_angle_radians);
        }
    }
    get_world_direction() {
        if (!this._parent) {
            return this._direction;
        }
        const parent_world_transform = calculate_world_transform(this._parent);
        return calculate_direction_from_parent_world_transform(parent_world_transform, this._direction);
    }
    create_debug_geometry() {
        const { line_manager, light_system, scene } = this._viewer;
        this._debug_geometry = new SpotLightVisualizer(this, line_manager, light_system.debug_color_mode, scene);
    }
    set_angles(inner, outer) {
        if (Math.min(inner, outer) < 0.0 || Math.max(inner, outer) > 2.0 * Math.PI) {
            console.warn('SpotLight angles are out of range. Clamping to 0 to 2pi (0 to 360 degrees)');
            inner = clamp(inner, 0, 2.0 * Math.PI);
            outer = clamp(outer, 0, 2.0 * Math.PI);
        }
        this._babylon_light.innerAngle = inner;
        this._babylon_light.angle = outer;
        // Make sure to run this after setting the angles on the babylon light, as that is where they're read from when validating the shadow angle.
        this.shadow.validate_and_warn_shadow_angle();
        if (this._intensity_mode === SpotLightIntensityMode.LUMEN) {
            // If we change the cone angle of the spotlight and it was set to use lumen instead of
            // candela, we need to reapply the intensity.
            const lumen = this._physical_intensity;
            // As the inner cone angle becomes smaller, the patch of light in the middle will become more intense.
            super.intensity = lumen / SpotLight.calculate_light_weighted_surface_area(this.inner_angle_radians, this.outer_angle_radians);
        }
        this.mark_dirty(Light.DIRTY_ANGLE);
    }
    static calculate_light_weighted_surface_area(inner_angle_radians, outer_angle_radians) {
        const half_inner = inner_angle_radians / 2.0;
        const half_outer = outer_angle_radians / 2.0;
        const inner_area = 2.0 * Math.PI * (1.0 - Math.cos(half_inner));
        // The outer area weighted by the penumbra, assuming the penumbra fade is linear in relation to cos(theta):
        // fade = 1.0 - (cos(theta) - cos(half_outer)) / (cos(half_inner) - cos(half_outer))
        // thus the weighted area of the outer (penumbra) part is then:
        //    integral of [cos(half_inner) - cos(x)] / [cos(half_inner) - cos(half_outer)] * 2pi * sin(x) dx from half_inner to half_outer
        //    = PI * (cos(half_outer) - cos(half_inner))
        const outer_weighted_area = Math.PI * (Math.cos(half_inner) - Math.cos(half_outer));
        return inner_area + outer_weighted_area;
    }
}
//# sourceMappingURL=SpotLight.js.map